"use client";

// Révélation "lettres qui apparaissent" des réponses BeBot, partagée entre le
// chat des challenges (ChallengeChat) et le chat des Reps (RepsAssistant).
// Extraite telle quelle de ChallengeChat : le réseau pousse des morceaux, mais
// les caractères sont RÉVÉLÉS un par un à cadence régulière via rAF (chaque
// lettre a son fondu, classe CSS .bebot-stream-char dans globals.css), et la
// révélation continue à son rythme après la fin du réseau, jamais coupée.
// `renderFinal(text)` : rendu une fois le message entièrement révélé (markdown
// côté challenges, texte enrichi côté Reps). Les messages jamais streamés
// (historique restauré) sautent l'animation.

import { useEffect, useLayoutEffect, useRef, useState } from "react";

export default function StreamingText({
    text,
    streaming,
    onReveal,
    className,
    renderFinal,
}) {
    const startedAnimating = useRef(streaming);
    const [revealed, setRevealed] = useState(() => (streaming ? 0 : text.length));

    const textRef = useRef(text);
    const streamingRef = useRef(streaming);
    const revealedRef = useRef(revealed);
    textRef.current = text;
    streamingRef.current = streaming;
    revealedRef.current = revealed;

    useEffect(() => {
        if (!startedAnimating.current) return;
        let raf = null;
        let last = null;
        let acc = 0;
        const tick = (now) => {
            if (last === null) last = now;
            acc += now - last;
            last = now;

            const total = textRef.current.length;
            let r = revealedRef.current;
            if (r < total) {
                const backlog = total - r;
                // La vitesse suit le retard accumulé et ralentit vers la fin
                // (borné : jamais poussif, jamais instantané).
                const cps = Math.min(130, Math.max(45, backlog * 1.4));
                const add = Math.floor(acc / (1000 / cps));
                if (add > 0) {
                    acc -= add * (1000 / cps);
                    r = Math.min(total, r + add);
                    revealedRef.current = r;
                    setRevealed(r);
                }
            } else {
                acc = 0;
            }

            // On boucle tant qu'il reste du texte à révéler ou que le réseau
            // peut encore en pousser.
            if (revealedRef.current < textRef.current.length || streamingRef.current) {
                raf = requestAnimationFrame(tick);
            } else {
                raf = null;
            }
        };
        raf = requestAnimationFrame(tick);
        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    // Suivre le texte fraîchement révélé (le parent garde le scroll en bas).
    // useLayoutEffect : après le DOM, avant le paint -> aucun saut visible.
    useLayoutEffect(() => {
        if (onReveal) onReveal();
    }, [revealed, onReveal]);

    // Tout est révélé et le réseau a fini : rendu final (markdown/enrichi).
    const settled = !streaming && revealed >= text.length;
    if (settled || !startedAnimating.current) {
        if (renderFinal) return renderFinal(text);
        return (
            <div className={className} style={{ whiteSpace: "pre-wrap" }}>
                {text}
            </div>
        );
    }

    return (
        <div className={`bebot-stream ${className || ""}`}>
            {Array.from(text.slice(0, revealed)).map((char, i) => (
                <span key={i} className="bebot-stream-char">
                    {char}
                </span>
            ))}
        </div>
    );
}
